// @flow
import React, {Fragment, useEffect, useMemo} from 'react';
import {View} from 'react-native';
import config from '../../../config';
import formatUnitType from '../../../lib/TripicaUnitTypeFormatter';
import I18n from '../../../lib/i18n';
import {OptionSlider} from '../../common/CharacteristicSlider';
import NewBadge from './NewBadge';
import RoamingTerritories from './RoamingTerritories';
import { Separator } from 'trpc-react-native-ui';

type Props = {
  creditableOptions: Object[][],
  selectedOptions: Object[],
  addOptionToNewOffer: Function,
  priceKey: string,
  orderType: string,
  isLocalDataZero?: boolean,
};

const RoamingPlanSliders = (props: Props) => {
  const filteredCreditableOptions = useMemo(() => {
    if (!props.creditableOptions) return [];

    return props.creditableOptions.map(creditableOption => {
      return creditableOption.filter(option => {
        let isOK = true;
        option?.characteristics?.forEach(charac => {
          if (charac.name === 'ORDER_CATEGORY_RESTRICTED') {
            if (!charac?.value.includes(props.orderType)) {
              isOK = false;
            }
          }
        });
        return isOK;
      });
    });
  }, [props.creditableOptions, props.orderType]);

  const filteredSelectedOptions = useMemo(() => {
    if (!props.selectedOptions) return [];

    return props.selectedOptions.map(selectedOption => {
      const allOptions = filteredCreditableOptions.flat();
      const foundOption = allOptions.find(
        (option: Object) => option.ouid === selectedOption.ouid,
      );

      if (!foundOption) {
        return allOptions.find(
          (option: Object) => option.type === selectedOption.type,
        );
      }
      return selectedOption;
    }).filter(Boolean);
  }, [props.selectedOptions, filteredCreditableOptions]);

  // Auto-select the first option for each group on mount, but only if Redux
  // doesn't already have a selection for this group (preserves user choices
  // when re-mounting across stepper steps)
  useEffect(() => {
    filteredCreditableOptions.forEach(options => {
      if (options.length > 0) {
        const alreadySelected = props.selectedOptions?.some(
          sel => options.some(opt => opt.ouid != null && opt.ouid === sel.ouid),
        );
        if (!alreadySelected) {
          props.addOptionToNewOffer(options[0]);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View>
      {filteredCreditableOptions.map((options, sliderIndex) => {
        if (options.length === 0) return null;

        const unitType = options[0].type;
        const formattedType = formatUnitType(unitType);
        const isDisabled = props.isLocalDataZero && formattedType.includes('DATA');

        return (
          <Fragment key={unitType}>
            <OptionSlider
              testID={`roaming-item-${sliderIndex}`}
              sliderTestID={`roaming-slider-${sliderIndex}`}
              options={options}
              displayIncludedForFirstOptionValue={
                config.displayIncludedForFirstOptionValue
              }
              onSelect={props.addOptionToNewOffer}
              name={unitType}
              selectedOptions={filteredSelectedOptions}
              priceKey={props.priceKey}
              badge={<NewBadge />}
              disabled={isDisabled}
            />
            <RoamingTerritories
              type={formattedType}
              selectedOption={filteredSelectedOptions.find(sel =>
                options.some(opt => opt.ouid != null && opt.ouid === sel.ouid),
              )}
            />
            <Separator crossAxisMargin={15} />
          </Fragment>
        );
      })}
    </View>
  );
};

export default RoamingPlanSliders;
